import { Datagrid, List, TextField, downloadCSV, TextInput, SelectInput, useListController } from 'react-admin';
import { AdminTherapistRequestResponseDto, AdminTherapistUserResponseDto, TherapistEducationInfoResponseDto, TherapistPersonalInfoResponseDto, TherapistTherapiesInfoResponseDto, TherapistWorkingInfoResponseDto } from 'shared/api';
import { LABELS } from 'shared/constants/labels';
import jsonExport from 'jsonexport/dist';
import { useEffect } from 'react';

const flatEducationInfo = (educationInfo: TherapistEducationInfoResponseDto) => {
  return {
    'educationInfo.education': educationInfo.education.map(note => note.university).join(','),
    'educationInfo.additionalEducation': educationInfo.additionalEducation?.map(note => note.organisation).join(',') || '',
  }
}

const flatPersonalInfo = (personalInfo: TherapistPersonalInfoResponseDto) => {
  return Object.entries(personalInfo).reduce((acc: any, entry) => {
    acc[`personalInfo.${entry[0]}`] = entry[1];
    return acc;
  }, {});
};

const flatTherapiesInfo = (therapiesInfo: TherapistTherapiesInfoResponseDto) => {
  return {
    'therapiesInfo.diseases': therapiesInfo.diseases.map(disease => disease.name).join(','),
    'therapiesInfo.excludedDiseases': therapiesInfo.excludedDiseases.join(','),
    'therapiesInfo.specialisation': therapiesInfo.specialisation.join(','),
    'therapiesInfo.therapyMethods': therapiesInfo.therapyMethods.map(method => method.name).join(','),
  }
}

const flatUser = (user: AdminTherapistUserResponseDto) => {
  return Object.entries(user).reduce((acc: any, entry) => {
    acc[`user.${entry[0]}`] = entry[1];
    return acc;
  }, {});
};

const flatWorkingInfo = (workingInfo: TherapistWorkingInfoResponseDto) => {
  const data = Object.entries(workingInfo).reduce((acc: any, entry) => {
    acc[`workingInfo.${entry[0]}`] = entry[1];
    return acc;
  }, {});

  return {
    ...data,
    'workingInfo.languages': workingInfo.languages.join(','),
    'workingInfo.workWithReligions': workingInfo.languages.join(','),
  }
}

const exporter = (requests: AdminTherapistRequestResponseDto[]) => {
  const data = requests.map(request => ({
    ...flatUser(request.user),
    ...flatPersonalInfo(request.personalInfo),
    ...flatWorkingInfo(request.workingInfo),
    ...flatTherapiesInfo(request.therapiesInfo),
    ...flatEducationInfo(request.educationInfo),
  }));

  jsonExport(data, (err, csv) => { downloadCSV(csv, 'requests') });
}

const SearchTermChoices = [
  { id: 'email', name: 'Email' },
];

const therapistRequestsFilters = [
  <TextInput 
    label="Строка для поиска" 
    placeholder='Строка для поиска' 
    source="searchValue" 
    parse={value => value?.replace(/[^а-яА-Яa-zA-Z\d.,@+\s]/g, '').trim()}
    validate={value => {
      if (value && !/^[а-яА-Яa-zA-Z][а-яА-Яa-zA-Z\s\d.,@+]*$/.test(value)) {
        return 'Строка должна начинаться с буквы, затем можно использовать буквы, пробелы, цифры и символы .,@+';
      }
      return undefined;
    }}
    alwaysOn 
  />,
  <SelectInput label="Искать по:"  source="searchTerm" choices={SearchTermChoices} emptyText="Имя" emptyValue="name" alwaysOn />,
];

export const TherapistsRequestsList = () => {
  const { setFilters } = useListController();

  useEffect(() => {
    setFilters(
      { 
        searchTerm: 'name',
        searchValue: '',
        booleanFilter: ''
      },
      {}
    );
  }, []);

  return (
    <List exporter={exporter} filters={therapistRequestsFilters} filterDefaultValues={{ searchTerm: 'name' }}>
      <Datagrid bulkActionButtons={false} rowClick="show">
        <TextField source="personalInfo.firstname" label={LABELS.firstname} />
        <TextField source="personalInfo.lastname" label={LABELS.lastname} />
      </Datagrid>
    </List>
  );
};
